// Takes in the communityWallets and sorts them by week and then by type of
// deposit.
import FindNextDay from "../../../../../functions/FindNextDay.js";
import YYYYMMDDConverter from "../../../../../functions/YYYYMMDDConverter.js";

// Parse a stored transaction date into a local Date. A YYYY-MM-DD string is
// built explicitly from its parts so it is anchored to local midnight rather
// than UTC midnight (which new Date() would otherwise do, shifting the day back
// in negative UTC offsets). Other formats fall back to the default parser.
function parseLocalTransactionDate(value) {
  if (typeof value === "string") {
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const [, y, m, d] = isoMatch;
      return new Date(Number(y), Number(m) - 1, Number(d));
    }
  }
  return new Date(value);
}

// summarise the wallet
export default function groupWalletTransactionsByWeek(communityWallets) {
  // This is a dictionary that contains the week {weekKey : {transactions}}
  const weeklySummary = {};
  // Cycle through all the communityWallets.
  communityWallets.forEach((wallet) => {
    // Set the user's wallet
    const { firstName, lastName, userId, transactionHistory = [] } = wallet;
    // Run through the transactionHistory
    transactionHistory.forEach((tx) => {
      // Check if the transaction exists and is not less than or equal to 0.  The amount
      // is abosolute number so should never be negative.
      if (!tx.amount || tx.amount <= 0) return;

      // Find the Saturday following this transaction date. Parse the stored
      // date as a local date so YYYY-MM-DD values (which JS would otherwise
      // read as UTC midnight) don't shift back a day in negative UTC offsets
      // and land a boundary transaction in the wrong week.
      const txDate = parseLocalTransactionDate(tx.date);
      const endOfWeek = FindNextDay(txDate, 6);
      const endOfWeekKey = YYYYMMDDConverter(endOfWeek);
      // Check to make sure the key exists.  If not then create it.
      if (!weeklySummary[endOfWeekKey]) {
        weeklySummary[endOfWeekKey] = {
          ETransfer: 0,
          Cash: 0,
          F2PMCredits: 0,
          Refunds: 0,
          Mileage: 0,
          Packaging: 0,
          Misc: 0,
          CreditCard: 0,
          transactions: [],
        };
      }

      // Decide sign based on transaction type
      const sign = tx.transactionType?.toLowerCase() === "debit" ? -1 : 1;

      // Build a detailed entry
      const entry = {
        userId,
        name: `${firstName} ${lastName}`,
        type: tx.transactionType, // credit / debit
        method: tx.paymentMethod || "Not Applicable",
        description: tx.description || "",
        date: tx.date,
        amount: sign * tx.amount,
        location: tx.location,
        collectedDate: tx.collectedDate,
      };
      // push the entry into the list.
      weeklySummary[endOfWeekKey].transactions.push(entry);
      // Check the method and then add that as well.
      const method = (tx.paymentMethod || "").toLowerCase().trim();

      // Map variants to canonical bucket names
      const methodMap = {
        etransfer: "ETransfer",
        cash: "Cash",
        "cash deposit": "Cash",
        "f2pm credit": "F2PMCredits",
        f2pmcredits: "F2PMCredits",
        refund: "Refunds",
        mileage: "Mileage",
        packaging: "Packaging",
        creditcard: "CreditCard",
      };

      const bucket = methodMap[method] || "Misc";

      // Apply with sign
      weeklySummary[endOfWeekKey][bucket] += sign * tx.amount;
    });
  });

  // Return dates sorted descending (newest first)
  const sortedWeeks = Object.keys(weeklySummary).sort().reverse();

  return { weeklySummary, sortedWeeks };
}
